import { NOTE_IDENTIFICATION } from '@cavaquinho/shared';
import { registerAnswerDescriber, registerMissDescriber, registerRenderer } from '../registry.ts';
import { NoteIdentificationRenderer } from './NoteIdentificationRenderer.tsx';
import { isNoteIdentificationFeedback } from './Feedback.tsx';
import { noteLabel } from '../../../lib/notes.js';

/**
 * The one registration site for this Exercise Type's presentation
 * (contracts/exercise-type-registry.md § 2). `features/exercise-types/index.ts`
 * imports this module for the side effects below; nothing else may call
 * `registerRenderer('note_identification', …)`.
 */
registerRenderer(NOTE_IDENTIFICATION, NoteIdentificationRenderer);

/**
 * How this type names a miss on the session summary: the note that sounded,
 * and the note that was heard instead.
 *
 * The guard is the same one the feedback panel uses, for the same reason —
 * `publicFeedback` arrives as `unknown` off the wire, and a payload this
 * build does not recognise contributes no line rather than a broken one. A
 * hit describes nothing: the summary lists what got away.
 */
registerMissDescriber(NOTE_IDENTIFICATION, (feedback, locale) => {
  if (!isNoteIdentificationFeedback(feedback)) return null;
  const { correctNote, selectedNote } = feedback;
  if (correctNote === selectedNote) return null;

  return {
    token: noteLabel(correctNote, locale),
    detail: `ouviu ${noteLabel(selectedNote, locale)}`,
  };
});

/**
 * How this type names a recorded answer on the Lesson review — every answer,
 * right ones included, which is what separates it from the miss describer
 * above.
 *
 * The played note IS the question for this type, so it is also what a review
 * row leads with: `publicFeedback.correctNote` is the note that sounded, and
 * the learner recognises the row by it. The public prompt carries only an
 * audio URL and the options that were offered, neither of which reads as a
 * question in a list, so nothing is taken from it.
 */
registerAnswerDescriber(NOTE_IDENTIFICATION, (_publicPrompt, feedback, locale) => {
  if (!isNoteIdentificationFeedback(feedback)) return null;
  const { correctNote, selectedNote } = feedback;
  const correct = noteLabel(correctNote, locale);

  return {
    prompt: null,
    token: correct,
    detail:
      correctNote === selectedNote
        ? 'você acertou'
        : `você respondeu ${noteLabel(selectedNote, locale)}`,
  };
});
